import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { labelMatchesSearch } from '../utils/searchMatch';

/**
 * Only one SearchableSelect may be open at a time.
 *
 * Each instance keeps its own `isOpen` and coordinated with the others solely
 * through a `mousedown` listener. That handles a mouse click on another
 * trigger, but the keyboard path never emits mousedown — `handleTriggerKeyDown`
 * opens on ArrowDown / ArrowUp / Enter / Space — so tabbing to the next select
 * and pressing a key left BOTH menus open, overlapping. Reported on the Enroll
 * Client screen with Primary Payer and Insurance Type stacked on top of one
 * another.
 *
 * A registry fixes every route into the open state at once, rather than adding
 * a second listener that would only cover one more of them. Entries are the
 * instances' own close functions, removed on unmount so a stale entry can never
 * keep a later dropdown shut.
 */
const mountedSelects = new Set();
const closeOtherSelects = (self) => {
  mountedSelects.forEach((close) => {
    if (close !== self) close();
  });
};

export default function SearchableSelect({
  id,
  value,
  onChange,
  options = [],
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  className = "",
  required = false,
  disabled = false,
  ariaLabelledBy,
  dropUp = false,
  // Text size. 10px is the clinical-form standard and stays the default; pages
  // whose other fields use the page's own size pass 'inherit' so the dropdown
  // matches the native inputs beside it.
  fontSize = '10px',
  // Optional: hear what was typed. For lists that are a first page from the
  // server, the consumer fetches the matching page; local filtering continues.
  onSearchChange = null,
  // Optional: the consumer's server search is still running. An empty list
  // then reads "Searching…" rather than a premature "No matches found".
  searching = false,
  // Optional: what an empty list says instead of "No matches found" (for
  // example, where to create the missing record).
  emptyContent = null
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;

  // Filter options based on search term. Derived, never mirrored into state:
  // most callers build `options` inline, so it is a new array on every parent
  // render. Copying it into state from an effect set state after every
  // keystroke in the parent form, and fast typing in a Diagnostic Assessment
  // tripped React's nested update limit (Sentry ARASYNC-WEB-17, 13 Oct 2026).
  // Words match in any order, so a full name finds the client whichever way
  // round it is typed ("Daniel Lee" / "Lee Daniel").
  const filteredOptions = useMemo(() => (
    searchTerm
      ? options.filter(option => labelMatchesSearch(option.label, searchTerm))
      : options
  ), [searchTerm, options]);

  // Reset highlight when search term changes, and tell the consumer
  useEffect(() => {
    setHighlightedIndex(-1);
    if (typeof onSearchChangeRef.current === 'function') onSearchChangeRef.current(searchTerm);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Join the single-open registry for this component's lifetime.
  const closeSelf = useCallback(() => setIsOpen(false), []);
  useEffect(() => {
    mountedSelects.add(closeSelf);
    return () => { mountedSelects.delete(closeSelf); };
  }, [closeSelf]);

  // Open this one and shut every other, whatever route got us here.
  const openDropdown = useCallback(() => {
    closeOtherSelects(closeSelf);
    setIsOpen(true);
    setSearchTerm('');
    setHighlightedIndex(-1);
  }, [closeSelf]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selectedOption = options.find(option => option.value === value);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const toggleDropdown = () => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
    } else {
      openDropdown();
    }
  };

  const handleTriggerKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (!isOpen) return;
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setHighlightedIndex(-1);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
      } else {
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        openDropdown();
      } else {
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex].value);
      } else {
        toggleDropdown();
      }
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setHighlightedIndex(-1);
      triggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex].value);
      }
    }
  };

  return (
    <div className="relative w-full max-w-full" ref={dropdownRef}>
      {/* Selected Value Display */}
      <div
        id={id}
        ref={triggerRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled}
        aria-label={ariaLabelledBy ? undefined : placeholder || 'Select an option'}
        aria-labelledby={ariaLabelledBy}
        className={`
          app-select-control w-full max-w-full box-border rounded-lg px-2 py-1 cursor-pointer
          flex items-center justify-between
          ${disabled ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800'}
          ${className}
        `}
        onClick={toggleDropdown}
        tabIndex={disabled ? -1 : 0}
        style={{ fontSize }}
        onKeyDown={handleTriggerKeyDown}
      >
        <span
          className={`truncate ${selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}
          style={{ fontSize }}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Clearing sets ''; offering it while '' (an "All ..." option) is chosen did nothing. */}
          {selectedOption && !disabled && value !== '' && value != null && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 p-0.5"
              aria-label="Clear selection"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg
            className={`w-3 h-3 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`animate-dropdown absolute z-[150] w-full max-w-full box-border bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden ${dropUp ? 'bottom-full mb-1' : 'mt-1'}`}
          style={{maxHeight: '180px', left: 0, right: 0}}
        >
          {/* Search Input */}
          <div className="p-1.5 border-b border-gray-200 dark:border-gray-700">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search options"
              className="w-full max-w-full box-border px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              style={{ fontSize }}
            />
          </div>

          {/* Options List */}
          <div
            role="listbox"
            aria-label="Options"
            className="overflow-y-auto"
            style={{maxHeight: '144px'}}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-2 py-1.5 text-gray-500 dark:text-gray-400" style={{ fontSize }}>
                {searching ? 'Searching…' : emptyContent || (searchTerm || options.length > 0 ? 'No matches found' : 'No options available')}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  className={`
                    px-2 py-1.5 cursor-pointer truncate text-gray-700 dark:text-gray-300
                    ${option.muted ? 'opacity-60' : ''}
                    hover:bg-gray-50 dark:hover:bg-gray-700
                    ${value === option.value ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-900 dark:text-blue-100' : ''}
                    ${index === highlightedIndex ? 'bg-blue-100 dark:bg-blue-900/50' : ''}
                  `}
                  style={{ fontSize }}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Hidden input for form validation */}
      {required && (
        <input
          type="hidden"
          value={value || ''}
          required={required}
        />
      )}
    </div>
  );
}
